import { Extension, JSONDocument, ReaderContext, WriterContext, vec3, Node, GLTF, BufferUtils, PropertyType, GLB_BUFFER } from "@gltf-transform/core3";
import { EXT_LIGHTS_IES, IES_PROFILE_MIME_TYPE } from "../constants";
import { LightProfile } from "./light-profile";
import { LightInstance } from "./light-instance";

const NAME = EXT_LIGHTS_IES;

/**
 * The root definition for the EXT_lights_ies extension. Light profiles are defined within the array lights which is
 * defined in the extensions.EXT_lights_ies property located in the top-level object of the asset.
 */
export interface LightsIESRootDef {
	lights?: Array<LightProfileDef>;
}

/**
 * Each light profile defines either the `uri` property, which contains the URI (or IRI) of an external IES file or a
 * data-URI with embedded data, or the `bufferView` property, which points to a bufferView object that contains an IES
 * light profile file. If the `bufferView` property is used then `mimeType` must also be defined and have the value
 * `application/x-ies-lm-63`.
 */
export type LightProfileDef = LightProfileUriDef | LightProfileBufferViewDef;

export interface LightProfileUriDef {
	/** The URI (or IRI) of the light profile. */
	uri: string;
	/** glTF name of the light profile. */
	name?: string;
}

export interface LightProfileBufferViewDef {
	/** The index of the bufferView that contains the IES light profile. */
	bufferView: number;
	/** The light profile's media type. Must be "application/x-ies-lm-63". */
	mimeType: typeof IES_PROFILE_MIME_TYPE;
	/** glTF name of the light profile. */
	name?: string;
}

/**
 * Light profiles can be attached to a node by defining the extensions.EXT_lights_ies property and, within that, the
 * light property which defines an index into the lights array. Optionally, the multiplier and color properties can be
 * defined to scale the light's intensity and tint the light, respectively. The RGB value of the color property is in
 * linear space, clamped to the [0, 1] range, and acts as a per color channel multiplier. Note that values other than
 * 1.0 for multiplier and (1.0, 1.0, 1.0) for color change the energy output of the light source. An energy preserving
 * tint can be achieved by modifying multiplier to compensate for the energy loss: multiplier *= 3 / dot(color, color).
 */
export interface LightProfileInstanceDef {
	/** Index of the light profile. */
	light: number;
	/** Non-negative factor to scale the light's intensity.	*/
	multiplier?: number;
	/** RGB value for the light's color in linear space. */
	color?: vec3;
}

/** Map of light profile byte arrays by light profile index */
type BufferViewData = { [key: string]: Uint8Array };

/**
 * Light profiles are defined within the array `lights` which is defined in the `extensions.EXT_lights_ies` property
 * located in the top-level object of the asset.
 * 
 * Each light profile defines either the `uri` property, which contains the URI (or IRI) of an external IES file or a
 * data-URI with embedded data, or the `bufferView` property, which points to a bufferView object that contains an IES
 * light profile file. If the `bufferView` property is used then `mimeType` must also be defined and have the value
 * `application/x-ies-lm-63`.
 * 
 * @see https://github.com/KhronosGroup/glTF/tree/main/extensions/2.0/Vendor/EXT_lights_ies
 */
export class EXTLightsIES extends Extension {
	public readonly extensionName = NAME;
	public readonly prewriteTypes = [PropertyType.BUFFER];

	public static readonly EXTENSION_NAME = NAME;

	private _bufferViewData: BufferViewData = {};

	/** Creates a new LightProfile property for the root extension and to be referenced by {@link LightInstance}. */
	public createLightProfile(name: string = ""): LightProfile {
		return new LightProfile(this.document.getGraph(), name);
	}

	public removeLightProfile(lightProfile: LightProfile): this {
		if (this.properties.has(lightProfile)) {
			this.properties.delete(lightProfile);
		}
		return this;
	}

	public listLightProfiles(): Array<LightProfile> {
		return Array.from(this.properties).filter((prop) => prop instanceof LightProfile) as Array<LightProfile>;
	}

	/** Creates a new LightInstance property for use on a {@link Node}. */
	public createLightInstance(name: string = ""): LightInstance {
		return new LightInstance(this.document.getGraph(), name);
	}

	public read(context: ReaderContext): this {
		const jsonDoc: JSONDocument = context.jsonDoc;

		if (!jsonDoc.json.extensions || !jsonDoc.json.extensions[NAME] || !jsonDoc.json.nodes) return this;

		const rootDef: LightsIESRootDef = jsonDoc.json.extensions[NAME] as LightsIESRootDef;
		const lightProfileDefs: Array<LightProfileDef> = rootDef.lights || ([] as Array<LightProfileDef>);
		const lightProfiles: Array<LightProfile> = lightProfileDefs.map((lightProfileDef: LightProfileDef) => {
			const lightProfile: LightProfile = this.createLightProfile(lightProfileDef.name);

			if (this.isLightProfileUriDef(lightProfileDef)) {
				lightProfile.setUri(lightProfileDef.uri);
			} else if (this.isLightProfileBufferViewDef(lightProfileDef)) {
				const bufferViewIndex = lightProfileDef.bufferView;
				const bufferViewDef = jsonDoc.json.bufferViews![bufferViewIndex];
				const bufferDef = jsonDoc.json.buffers![bufferViewDef.buffer];
				const resource = bufferDef.uri ? jsonDoc.resources[bufferDef.uri] : jsonDoc.resources[GLB_BUFFER];
				const source = BufferUtils.toView(resource, bufferViewDef.byteOffset, bufferViewDef.byteLength);
				const iesProfile = BufferUtils.decodeText(source).replaceAll('\0', ''); // remove trailing NUL added from BufferUtils.pad
			
				lightProfile.setIesProfile(iesProfile);
				lightProfile.setBufferView(bufferViewIndex);
				lightProfile.setMimeType(lightProfileDef.mimeType);
			}

			return lightProfile;
		});

		lightProfiles.forEach((lightProfile: LightProfile) => {
			this.properties.add(lightProfile);
		});

		jsonDoc.json.nodes.forEach((nodeDef: GLTF.INode, nodeIndex: number) => {
			if (!nodeDef.extensions || !nodeDef.extensions[NAME]) return;
			const lightNodeDef: LightProfileInstanceDef = nodeDef.extensions[NAME] as LightProfileInstanceDef;
			const light: LightInstance = this.createLightInstance();
			light.setLightProfile(lightProfiles[lightNodeDef.light]);
			if (lightNodeDef.multiplier !== undefined) {
				light.setMultiplier(lightNodeDef.multiplier);
			}
			if (lightNodeDef.color !== undefined) {
				light.setColor(lightNodeDef.color);
			}
			context.nodes[nodeIndex].setExtension(NAME, light);
		}, this);

		return this;
	}

	public prewrite(context: WriterContext, propertyType: PropertyType): this {
		if (propertyType === PropertyType.BUFFER) {
			this._prewriteBuffers(context);
		}
		return this;
	}

	private _prewriteBuffers(context: WriterContext): void {
		const buffer = this.document.getRoot().listBuffers()[0] || this.document.createBuffer();

		if (!context.otherBufferViews.has(buffer)) {
			context.otherBufferViews.set(buffer, []);
		}

		this._bufferViewData = {};

		this.listLightProfiles().forEach((lightProfile: LightProfile, index) => {
			const iesProfile: string | null = lightProfile.getIesProfile();
			if (iesProfile == null) {
				return;
			}

			const binaryData = BufferUtils.pad(BufferUtils.encodeText(iesProfile));
			this._bufferViewData[index] = binaryData;
			const otherBufferViews = context.otherBufferViews.get(buffer) || [];
			otherBufferViews.push(binaryData);
			context.otherBufferViews.set(buffer, otherBufferViews);
		});
	}

	public write(context: WriterContext): this {
		const jsonDoc: JSONDocument = context.jsonDoc;

		if (this.properties.size === 0) return this;

		const lightProfileIndexMap: Map<LightProfile, number> = new Map<LightProfile, number>();
		const lightProfileDefs: Array<LightProfileDef> = [];

		this.listLightProfiles().forEach((lightProfile: LightProfile, index) => {
			let lightProfileDef: LightProfileDef;
			if (lightProfile.getUri() != null) {
				lightProfileDef = {
					uri: lightProfile.getUri(),
				};
			} else if (lightProfile.getIesProfile() != null) {
				const buffer = this.document.getRoot().listBuffers()[0] || this.document.createBuffer();
				const otherBufferViews = context.otherBufferViews.get(buffer);
				let bufferViewIndex: number = -1;

				// Find the bufferView index for this light profile data.
				otherBufferViews?.find((data: Uint8Array) => {
					if (data === this._bufferViewData[index]) {
						const id = context.otherBufferViewsIndexMap.get(data);
						if (id) {
							bufferViewIndex = id;
							return true;
						}
					}
				});
				if (bufferViewIndex < 0) {
					throw new Error("Could not find bufferView index for light profile.");
				}

				lightProfileDef = {
					bufferView: bufferViewIndex,
					mimeType: lightProfile.getMimeType(),
				};
			} else {
				throw new Error("EXT_lights_ies LightProfile must have either a URI or a bufferView defined.");
			}

			if (lightProfile.getName()) {
				lightProfileDef.name = lightProfile.getName();
			}

			lightProfileDefs.push(lightProfileDef);
			lightProfileIndexMap.set(lightProfile, lightProfileDefs.length - 1);
		});

		this.document
			.getRoot()
			.listNodes()
			.forEach((node: Node) => {
				const light: LightInstance | null = node.getExtension<LightInstance>(NAME);
				if (light) {
					const nodeIndex: number = context.nodeIndexMap.get(node)!;
					const nodeDef: GLTF.INode = jsonDoc.json.nodes![nodeIndex];
					nodeDef.extensions = nodeDef.extensions || {};

					const lightProfile: LightProfile | null = light.getLightProfile();
					if (!lightProfile) return;
					const lightInstanceDef: LightProfileInstanceDef = { light: lightProfileIndexMap.get(lightProfile) } as LightProfileInstanceDef;
					if (light.getMultiplier() !== 1) {
						lightInstanceDef.multiplier = light.getMultiplier();
					}
					if (light.getColor() && !light.getColor().every((c) => c === 1)) {
						lightInstanceDef.color = light.getColor();
					}
					nodeDef.extensions[NAME] = lightInstanceDef;
				}
			});

		jsonDoc.json.extensions = jsonDoc.json.extensions || {};
		jsonDoc.json.extensions[NAME] = { lights: lightProfileDefs };

		return this;
	}

	private isLightProfileUriDef(lightProfileDef: LightProfileDef): lightProfileDef is LightProfileUriDef {
		return (lightProfileDef as LightProfileUriDef).uri !== undefined;
	}

	private isLightProfileBufferViewDef(lightProfileDef: LightProfileDef): lightProfileDef is LightProfileBufferViewDef {
		return (lightProfileDef as LightProfileBufferViewDef).bufferView !== undefined;
	}
}